import React, { useMemo, useRef, useEffect, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MachineEvent } from "@/machines/types/event";
import { useTimelineContext } from "@/machines/context/timeline-context.tsx";
import { useSelectedTime, useLastOrigin } from "@/machines/context/cursor-store.ts";
import { useTimelineZoom } from "../hooks/useTimelineZoom";
import { useTimelinePan } from "../hooks/useTimelinePan";
import { useTimelineDrag } from "../hooks/useTimelineDrag";
import { useTimelineCursor } from "../hooks/useTimelineCursor";
import { useTimelineEvents, type EventSegment } from "../hooks/useTimelineEvents";
import { useTimelineScrollbar } from "../hooks/useTimelineScrollbar";
import { useTimelineLabels } from "../hooks/useTimelineLabels";
import { useVisibleXRange } from "../hooks/useVisibleXRange";
import { useDayBoundaries } from "../hooks/useDayBoundaries";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import { PendingRanges } from "../components/PendingRanges";
import { useLiveTimelineDomain } from "../hooks/useLiveTimelineDomain";
import { useEventResize } from "../hooks/useEventResize";
import { useEventMove } from "../hooks/useEventMove";
import { getTimestampBounds, timeToX } from "../utils/timelineCalculations";
import { TIME_AXIS_HEIGHT, AUTO_FOCUS_ZOOM } from "../utils/timelineConstants";
import { useTimelineEditQueueStore } from "@/machines/stores/useTimelineEditQueueStore";
import { DIG_LAYER_STATES, SNAPPABLE_EVENT_LAYERS } from "../config/snapConfig";
import { getEventUniqueId, findOverlappingEvents } from "../utils/timelineHelpers";
import { TimelineTrack } from "../components/TimelineTrack";
import { VideoTrack } from "../components/VideoTrack";
import { CursorLines } from "../components/CursorLines";
import { EventTooltip } from "../components/TimelineTooltips";
import { cn } from "@/ui/lib/utils";

interface TimelineSegment {
  start: number;
  end: number;
  hasVideo: boolean;
}

interface TimelineEventsProps {
  tracks: EventSegment[][];
  width?: number;
  isLoading?: boolean;
  isEmpty?: boolean;
  videoSegments?: TimelineSegment[];
  /** Window stretches never probed for footage — drawn as "not asked yet". */
  videoPendingRanges?: { from: number; to: number }[];
  /**
   * Stretches of the visible window whose events have not been fetched yet —
   * drawn as loading rather than left looking empty. An empty stretch we HAVE
   * fetched is a fact about the machine; one we have not is a fact about us, and
   * only the second one is going to change.
   */
  pendingRanges?: { from: number; to: number }[];
  /**
   * A refresh is in flight over segments already on screen. Held back by
   * `useDelayedLoading`, so an ordinary 250 ms poll changes nothing and only a
   * genuinely slow one is shown — the case where an operator opening a fresh
   * alert is looking at segments that have not caught up with it yet.
   */
  isRefreshing?: boolean;
  onEventDoubleClick?: (event: MachineEvent) => void;
  /**
   * Non-edit-mode click on a segment — opens the event's detail (e.g. the map's
   * on-map card). Truck-cycle segments report `isTruckCycle: true` (their event
   * is a TruckEvent cast to MachineEvent). The click still bubbles to the SVG
   * for cursor freeze.
   */
  onEventOpen?: (event: MachineEvent, isTruckCycle: boolean) => void;
  /**
   * Live mode: instead of fitting the axis to the event extents, show a
   * rolling window of this many ms anchored to the wall clock and advancing
   * continuously (auto-scrolling left). Zoom/pan are locked — the viewport is
   * exactly the window — and event rects get CSS transitions so movement and
   * in-progress growth are smooth.
   */
  liveViewSpanMs?: number;
  /**
   * Live mode: fraction (0–1) of the visible span reserved as empty "future"
   * lead space to the right of the live edge, so `now` sits at
   * `(1 - liveViewLeadFraction)` of the width (e.g. 0.2 → 80%). Defaults to `0`
   * (edge pinned to the right). See {@link useLiveTimelineDomain}.
   */
  liveViewLeadFraction?: number;
  /**
   * Map live-follow only: when set (0–1), anchor the live domain on the CURSOR
   * (the video's live playback position) at this fraction of the width instead of
   * on `now` — e.g. 0.75 pins the cursor at 75%, leaving the ~25% to its right for
   * the newest (video-less) events that lead footage by the upload lag. Supersedes
   * `liveViewLeadFraction` when set. See {@link useLiveTimelineDomain}.
   */
  liveCursorAnchor?: number;
  /**
   * Live-mode ALERT-REVIEW override (ms). When set, the domain freezes to a fixed
   * window CENTERED on this moment instead of the rolling live window — so a
   * selected alert/event (and the cursor) stays visible even when it's older than
   * the live span. Null returns to the rolling live domain. Only applies in live
   * mode (`liveViewSpanMs` set); ignored otherwise.
   */
  reviewTime?: number | null;
  /**
   * Render the bottom time axis / tick labels. Defaults to `true`. The map's
   * stacked live timelines set this to `false` on every row except the last so
   * a single shared axis renders once beneath the stack (all rows share the
   * same time domain, so the bottom axis lines up under every row's segments).
   */
  showTimeAxis?: boolean;
  /**
   * Live domain computed ONCE for a whole stack of timelines, overriding this
   * one's own rolling computation.
   *
   * The map stacks a row per machine under a single shared axis. Each row deriving
   * its own domain from its own cursor store put the same instant at a different
   * x per row, so nothing lined up with the axis or with the other rows. When this
   * is set the row adopts the stack's domain verbatim.
   */
  liveDomainOverride?: { min: number; max: number } | null;
  /**
   * The instant to draw the PLAYBACK cursor at, overriding this timeline's own
   * `selectedTime`. Stacked rows share one playhead — an inactive row's private
   * cursor is a seed that nothing advances, so left to itself it renders a
   * playhead frozen somewhere unrelated to the one the operator is watching.
   * Affects only where the line is drawn; interaction still uses the real cursor.
   */
  sharedPlaybackMs?: number | null;
}


/** Row height used for loading placeholders — the ordinary track row height. */
const SKELETON_ROW_HEIGHT = 58;
/** Rows to show when nothing has ever loaded, so there is no last count to keep. */
const DEFAULT_SKELETON_ROWS = 3;

const TimelineEvents = ({
  tracks,
  width = 1000,
  isLoading = false,
  videoSegments = [],
  videoPendingRanges = [],
  pendingRanges = [],
  isRefreshing = false,
  onEventDoubleClick,
  onEventOpen,
  liveViewSpanMs,
  liveViewLeadFraction,
  liveCursorAnchor,
  reviewTime,
  showTimeAxis = true,
  liveDomainOverride = null,
  sharedPlaybackMs = null,
}: TimelineEventsProps) => {
  const { t } = useTranslation("machines");
  const {
    setSelectedTime,
    selectedTrack,
    setSelectedTrack,
    isTrackLocked,
    setIsTrackLocked,
    isEditMode,
    selectedEventForEdit,
    setSelectedEventForEdit,
    createStartTime,
    setCreateStartTime,
    isVideoPlaying,
    isVideoLinked,
    centerRequest,
  } = useTimelineContext();
  // The cursor is the ONE legitimate per-frame render on the timeline: the
  // playhead X derives from `selectedTime`, so this component re-renders per tick
  // (via the selector) while every other consumer stays quiet. `lastOrigin`
  // changes only on origin transitions, so its selector adds no per-tick renders.
  const selectedTime = useSelectedTime();
  const lastOrigin = useLastOrigin();

  const containerRef = useRef<HTMLDivElement>(null);
  const timeAxisRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Calculate row heights based on track segments
  /**
   * Rows to draw while the tracks are loading and none have arrived yet.
   *
   * `totalHeight` derives from `tracks`, so an empty list collapsed the whole
   * area to nothing: the tracks did not look like they were loading, they looked
   * like they had been removed, and the timeline jumped as they came back. That
   * is worst exactly when it is most confusing — returning to the tab, switching
   * machine, or any refetch that briefly empties the list.
   *
   * Held at the LAST known count so the layout keeps its shape across a reload
   * instead of guessing a number and resizing twice.
   */
  const lastTrackCountRef = useRef(0);
  if (tracks.length > 0) lastTrackCountRef.current = tracks.length;
  const skeletonRowCount =
    tracks.length === 0 && isLoading ? Math.max(1, lastTrackCountRef.current || DEFAULT_SKELETON_ROWS) : 0;

  const trackRowHeights = useMemo(() => {
    if (skeletonRowCount > 0) {
      return Array.from({ length: skeletonRowCount }, () => SKELETON_ROW_HEIGHT);
    }
    return tracks.map((track) => {
      if (track.length === 0) return 0;
      // Check if track is hidden
      const isHidden = track[0]?.hidden === true;
      if (isHidden) return 0; // Hidden tracks take no space
      const maxHeight = Math.max(...track.map((seg: EventSegment) => seg.trackHeight || 50));
      return maxHeight + 8; // height + padding
    });
  }, [tracks, skeletonRowCount]);

  const totalHeight = useMemo(() => {
    return trackRowHeights.reduce((sum, height) => sum + height, 0);
  }, [trackRowHeights]);

  // Calculate timestamp bounds
  const allTimestamps = useMemo(
    () => tracks.flat().flatMap((seg) => [seg.start, seg.end]),
    [tracks]
  );

  const isLive = liveViewSpanMs != null;
  // Editing needs a stable axis under the pointer, so the advance pauses. Alert
  // review also pauses it (its rolling value is unused while the review domain
  // overrides, and pausing avoids a per-tick re-render).
  // Disabled when the stack supplies a shared domain — one timer for the stack
  // instead of one per row, and no chance of the rows drifting a tick apart.
  const ownLiveDomain = useLiveTimelineDomain({
    enabled: isLive && liveDomainOverride == null,
    spanMs: liveViewSpanMs ?? 0,
    paused: isEditMode || reviewTime != null,
    leadFraction: liveViewLeadFraction,
    // Map live-follow: anchor on the cursor (video position) so it stays pinned
    // and visible; the newest video-less events sit to its right.
    anchorMs: liveCursorAnchor != null ? selectedTime : null,
    anchorFraction: liveCursorAnchor ?? null,
  });
  const liveDomain = liveDomainOverride ?? ownLiveDomain;

  // Alert-review override: a FIXED window centered on the reviewed moment so the
  // alert/event + cursor are visible even when older than the live span. Takes
  // precedence over the rolling live domain; null → rolling live.
  const reviewDomain = useMemo(() => {
    if (!isLive || reviewTime == null || liveViewSpanMs == null) return null;
    const half = liveViewSpanMs / 2;
    return { min: reviewTime - half, max: reviewTime + half };
  }, [isLive, reviewTime, liveViewSpanMs]);

  const [dataMin, dataMax] = useMemo(() => getTimestampBounds(allTimestamps), [allTimestamps]);
  const activeDomain = reviewDomain ?? liveDomain;
  const min = activeDomain?.min ?? dataMin;
  const max = activeDomain?.max ?? dataMax;

  // In live mode the fetch window is far wider than the view — only mount
  // segments that intersect the visible domain (plus a little slack so events
  // slightly ahead of the anchor still slide in from the right).
  const renderTracks = useMemo(() => {
    if (!isLive) return tracks;
    const slackMs = 30_000;
    return tracks.map((track) =>
      track.filter((seg) => seg.end >= min - slackMs && seg.start <= max + slackMs)
    );
  }, [tracks, isLive, min, max]);

  // Initialize hooks
  const zoom = useTimelineZoom({ initialZoom: 50 });
  // Live mode locks zoom: the SVG stretches to the container, so the window
  // span is exactly the viewport and there is nothing to scroll.
  const scaledWidth = isLive ? width : width * zoom.zoom;
  // The pending band alone earns the track: a strip with nothing fetched yet is
  // exactly the case that needs to say so rather than look empty.
  const VIDEO_TRACK_HEIGHT = videoSegments.length > 0 || videoPendingRanges.length > 0 ? 30 : 0;
  const viewBoxHeight = totalHeight + VIDEO_TRACK_HEIGHT;

  const pan = useTimelinePan({ containerRef, timeAxisRef, zoom: zoom.zoom });

  const drag = useTimelineDrag({
    containerRef,
    timeAxisRef,
    onScrollRatioChange: pan.setScrollRatio,
  });

  const cursor = useTimelineCursor({
    svgRef,
    scaledWidth,
    min,
    max,
    selectedTime,
    playbackTimeOverride: sharedPlaybackMs,
    lastOrigin,
    onTimeSelect: setSelectedTime,
    onTrackUnlock: () => {
      setSelectedTrack(null);
      setIsTrackLocked(false);
    },
  });

  const events = useTimelineEvents({ svgRef });
  const scrollbar = useTimelineScrollbar({
    containerRef,
    onScrollRatioChange: pan.setScrollRatio,
  });
  // ONE visible-window reading, shared by the axis and the event tracks, so
  // they cull against exactly the same slice and only one scroll subscription
  // exists. Disabled in live mode, where the SVG is stretched to 100% and never
  // scrolls.
  const visibleRange = useVisibleXRange(containerRef, { enabled: !isLive, scaledWidth });
  const timeLabels = useTimelineLabels({ scaledWidth, min, max, live: isLive, visibleRange });
  // Only when the window actually crosses midnight — a same-day timeline stays
  // clean, which is the whole point of computing these rather than always
  // drawing a mark.
  const dayBreaks = useDayBoundaries({ min, max, scaledWidth, enabled: !isLive });
  const showPending = useDelayedLoading(pendingRanges.length > 0);
  // Same threshold as the pending band and the header badge, so the three never
  // disagree about whether the timeline is waiting.
  const showRefreshing = useDelayedLoading(isRefreshing);

  // Respond to center requests from video component — pan only, no zoom change
  useEffect(() => {
    if (centerRequest === 0 || selectedTime === null) return;
    const x = timeToX(selectedTime, min, max, scaledWidth);
    pan.centerOn(x);
  }, [centerRequest]);

  // Track which events would be overlapped during drag/resize (for visual preview)
  const [overlappingEventIds, setOverlappingEventIds] = useState<Set<string>>(new Set());

  // Track hover position in create mode for preview
  const [createHoverTime, setCreateHoverTime] = useState<number | null>(null);

  // Track hovered event in edit mode for video preview (press V to seek video to this event)
  const hoveredEventForPreviewRef = useRef<MachineEvent | null>(null);

  // Track if we just finished moving to prevent accidental create
  const justFinishedMovingRef = useRef(false);

  // Get all machine events for snapping (includes created events from splits)
  // Memoized to prevent recalculation on every render
  const allMachineEvents = useMemo(() => {
    return tracks.flatMap(track =>
      track
        .filter(seg => seg.event && seg.event.startTime && seg.event.endTime)
        .map(seg => seg.event)
    );
  }, [tracks]);

  // Pre-compute sorted snap boundaries for O(log n) binary search during drag/resize
  // This is a MAJOR performance optimization - we compute once, use many times
  const { startBoundaries, endBoundaries } = useMemo(() => {
    const starts: number[] = [];
    const ends: number[] = [];

    allMachineEvents.forEach(event => {
      if (!event || !event.startTime || !event.endTime) return;

      // Check if this is a DIG-layer event (valid for cycle boundaries)
      const eventLayer = event.eventLayer?.toLowerCase();
      const eventState = event.state?.toLowerCase();
      const isDIGLayerByEventLayer = eventLayer && SNAPPABLE_EVENT_LAYERS.includes(eventLayer as any);
      const isDIGLayerByState = eventState && DIG_LAYER_STATES.includes(eventState as any);

      if (!isDIGLayerByEventLayer && !isDIGLayerByState) return;

      // Pre-compute timestamps once
      const startTime = new Date(event.startTime).getTime();
      const endTime = new Date(event.endTime).getTime();

      starts.push(startTime);
      ends.push(endTime);
    });

    // Sort for binary search
    starts.sort((a, b) => a - b);
    ends.sort((a, b) => a - b);

    return { startBoundaries: starts, endBoundaries: ends };
  }, [allMachineEvents]);

  // Binary search to find closest value in sorted array - O(log n) instead of O(n)
  const findClosestInSortedArray = useCallback((arr: number[], target: number): number | null => {
    if (arr.length === 0) return null;

    let left = 0;
    let right = arr.length - 1;

    // Edge cases
    if (target <= arr[0]) return arr[0];
    if (target >= arr[right]) return arr[right];

    // Binary search
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);

      if (arr[mid] === target) return arr[mid];

      if (arr[mid] < target) {
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    // left now points to first element greater than target
    // right points to last element less than target
    // Compare distances to find closest
    const leftDist = left < arr.length ? Math.abs(arr[left] - target) : Infinity;
    const rightDist = right >= 0 ? Math.abs(arr[right] - target) : Infinity;

    return leftDist < rightDist ? arr[left] : arr[right];
  }, []);

  // Optimized snapping function using pre-computed sorted boundaries
  // CRITICAL: Only snaps to DIG-layer events. TRUCK-layer events are NOT valid.
  const snapToBoundary = useCallback((timestamp: number, _events: MachineEvent[], type: 'start' | 'end'): number | null => {
    // Use pre-computed sorted boundaries for O(log n) lookup
    const boundaries = type === 'start' ? startBoundaries : endBoundaries;
    return findClosestInSortedArray(boundaries, timestamp);
  }, [startBoundaries, endBoundaries, findClosestInSortedArray]);


  // Helper: find overlapping truck_cycle events for a given time range
  const findTruckCycleOverlaps = useCallback((editedId: string, start: number, end: number): string[] => {
    const truckCycleTrack = tracks.find(track => {
      const seg = track.find(s => s?.trackKey);
      return seg?.trackKey === 'truck_cycles' || seg?.trackKey === 'truck-cycle';
    });
    if (!truckCycleTrack) return [];
    return findOverlappingEvents(editedId, start, end, truckCycleTrack);
  }, [tracks]);

  const resize = useEventResize({
    svgRef,
    scaledWidth,
    min,
    max,
    snapToBoundary,
    machineEvents: allMachineEvents
  });
  const move = useEventMove({
    svgRef,
    scaledWidth,
    min,
    max,
    snapToBoundary,
    machineEvents: allMachineEvents
  });
  const { updateEvent, moveEvent, deleteEvent, hasEventChanges, getModifiedEventTimes, createEvent } = useTimelineEditQueueStore();

  // Live overlap preview: highlight events that would be deleted during drag/resize
  useEffect(() => {
    // During resize
    if (resize.isResizing && resize.resizingEvent && resize.tempEventTimes) {
      const editedId = getEventUniqueId(resize.resizingEvent);
      const ids = findTruckCycleOverlaps(editedId, resize.tempEventTimes.start, resize.tempEventTimes.end);
      setOverlappingEventIds(new Set(ids));
      return;
    }

    // During move
    if (move.isMoving && move.movingEvent && move.tempEventTimes) {
      const editedId = getEventUniqueId(move.movingEvent);
      const ids = findTruckCycleOverlaps(editedId, move.tempEventTimes.start, move.tempEventTimes.end);
      setOverlappingEventIds(new Set(ids));
      return;
    }

    // Not dragging - clear
    if (overlappingEventIds.size > 0) {
      setOverlappingEventIds(new Set());
    }
  }, [resize.isResizing, resize.tempEventTimes, move.isMoving, move.tempEventTimes]);

  // Memoized callbacks for TimelineTrack to prevent re-renders
  const handleEventClick = useCallback((event: MachineEvent) => {
    setSelectedEventForEdit(event);
    setCreateStartTime(null);
  }, [setSelectedEventForEdit, setCreateStartTime]);

  const handleEventDoubleClickCallback = useCallback((seg: EventSegment, timestamp: number) => {
    cursor.setLockedCursorTime(timestamp);
    setSelectedTime(timestamp, "internal");

    if (seg.trackKey) {
      setSelectedTrack(seg.trackKey);
      setIsTrackLocked(true);
    }

    if (seg.event && onEventDoubleClick) {
      const state = seg.event.state?.toLowerCase();
      if (state === 'dump' || state === 'dump-no-truck') {
        onEventDoubleClick(seg.event);
      }
    }
  }, [cursor, setSelectedTime, setSelectedTrack, setIsTrackLocked, onEventDoubleClick]);

  // Memoize SVG mouse leave handler
  const handleSvgMouseLeave = useCallback(() => {
    events.handleSVGMouseLeave();
    cursor.handleSVGMouseLeave();
    setCreateHoverTime(null);
    // Clear hovered event for preview
    hoveredEventForPreviewRef.current = null;
  }, [events, cursor]);

  // Handler for tracking hovered event in edit mode (for V key to seek video)
  const handleEventHoverForPreview = useCallback((seg: EventSegment) => {
    if (!isEditMode) {
      hoveredEventForPreviewRef.current = null;
      return;
    }

    const event = seg.event;
    if (!event) {
      hoveredEventForPreviewRef.current = null;
      return;
    }

    // Don't track the currently selected event being edited
    const eventId = getEventUniqueId(event);
    const selectedId = selectedEventForEdit ? getEventUniqueId(selectedEventForEdit) : null;
    if (eventId === selectedId) {
      hoveredEventForPreviewRef.current = null;
      return;
    }

    hoveredEventForPreviewRef.current = event;
  }, [isEditMode, selectedEventForEdit]);

  // Handler for clearing hovered event on leave
  const handleEventLeaveForPreview = useCallback(() => {
    hoveredEventForPreviewRef.current = null;
  }, []);

  // Handle external time selection (auto-focus) — only for explicit user actions like alert/map clicks
  // Video playback and next/prev video do NOT auto-pan; user clicks "Center" button for that
  //
  // Re-runs when the domain first becomes real: an external seek often arrives in
  // the SAME commit that mints a new query key (the map's alert review switches to
  // Range mode and seeks at once), so `min`/`max` are still the empty-track
  // fallback and centering on them lands nowhere near the target. Keying on
  // `hasData` (not on min/max, which churn on every poll) re-centers exactly once
  // when the events land, and never fights a user pan afterwards.
  const hasData = allTimestamps.length > 0;
  // Time to center on once the auto-focus zoom has been applied (see below).
  const pendingFocusRef = useRef<number | null>(null);
  useEffect(() => {
    if (selectedTime === null) return;
    if (lastOrigin !== "external") return;
    if (isLive) {
      // Live mode locks zoom/pan (the SVG is width:100%, and the review domain
      // already centers on the reviewed moment), so skip those. But STILL lock
      // the cursor to the reviewed moment while reviewing — otherwise the only
      // line drawn is the red playback cursor at the video position, which for a
      // fresh alert lags the alert (its footage hasn't uploaded yet), so the
      // timeline shows no marker on the alert. A go-live external seek has no
      // reviewTime, so it clears the lock — no frozen marker leaks into live.
      cursor.setLockedCursorTime(reviewTime != null ? selectedTime : null);
      return;
    }

    pendingFocusRef.current = selectedTime;
    zoom.setZoom(AUTO_FOCUS_ZOOM);
    cursor.setLockedCursorTime(selectedTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTime, lastOrigin, hasData, isLive, reviewTime]);

  // Centering must happen only once the auto-focus zoom is APPLIED, never in the
  // same effect that requests it. `setZoom` re-renders, and `useTimelinePan`'s
  // zoom-sync effect — declared earlier, so it runs first — then restores
  // `scrollRatio * maxScroll`. On a timeline that has never been scrolled (a fresh
  // mount, or one that was in live mode, where the SVG is width:100% and cannot
  // scroll) `scrollRatio` is still 0, so it hard-resets scrollLeft to 0 and wipes
  // a centering done in the same commit. Deferring also means `scaledWidth`
  // matches the zoom the pixel maths is done at. Runs on every commit (a ref check
  // + early return) because the zoom often does NOT change — a second external
  // seek finds it already at AUTO_FOCUS_ZOOM, and no dep would tick.
  useEffect(() => {
    const target = pendingFocusRef.current;
    if (target === null) return;
    if (zoom.zoom !== AUTO_FOCUS_ZOOM) return; // zoom change not committed yet
    pendingFocusRef.current = null;
    pan.centerOn(timeToX(target, min, max, scaledWidth));
  });

  // Set up zoom resize event listeners
  useEffect(() => {
    if (!zoom.isResizing) return;

    document.addEventListener("mousemove", zoom.handleMouseMove);
    document.addEventListener("mouseup", zoom.handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", zoom.handleMouseMove);
      document.removeEventListener("mouseup", zoom.handleMouseUp);
    };
  }, [zoom.isResizing]);

  // Set up event resize listeners
  useEffect(() => {
    if (!resize.isResizing) return;

    const handleMouseUp = () => {
      const result = resize.handleResizeEnd();

      // Set flag to prevent accidental create clicks right after resizing
      justFinishedMovingRef.current = true;
      setTimeout(() => {
        justFinishedMovingRef.current = false;
      }, 100); // Small delay to ignore the mouseup click

      if (result) {
        const uniqueId = getEventUniqueId(result.event);

        // Check if there are already modifications in the queue
        const existingMods = getModifiedEventTimes(uniqueId);

        // Use modified times if they exist, otherwise use original event times
        let originalStart, originalEnd;
        if (existingMods) {
          originalStart = parseInt(existingMods.startTime);
          originalEnd = parseInt(existingMods.endTime);
        } else {
          originalStart = new Date(result.event.startTime).getTime();
          originalEnd = new Date(result.event.endTime).getTime();
        }

        const hasChanged = result.newStart !== originalStart || result.newEnd !== originalEnd;

        // Only save if actually changed
        if (hasChanged) {
          // Ensure both boundaries are snapped to DIG-layer events
          // When resizing only one handle, the other stays at its original position
          // which may be on a truck event - re-snap it to the nearest DIG-layer boundary
          let finalStart = result.newStart;
          let finalEnd = result.newEnd;

          const snappedStart = snapToBoundary(finalStart, [], 'start');
          if (snappedStart !== null) finalStart = snappedStart;

          const snappedEnd = snapToBoundary(finalEnd, [], 'end');
          if (snappedEnd !== null) finalEnd = snappedEnd;

          updateEvent(
            uniqueId,
            finalStart,
            finalEnd
          );

          // Auto-delete overlapped events on the same track
          const overlappedIds = findTruckCycleOverlaps(uniqueId, finalStart, finalEnd);
          for (const id of overlappedIds) deleteEvent(id);
        }
      }
      setOverlappingEventIds(new Set());
    };

    document.addEventListener("mousemove", resize.handleResizeMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", resize.handleResizeMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resize.isResizing, updateEvent, snapToBoundary, findTruckCycleOverlaps, deleteEvent]);

  // Set up event move listeners
  useEffect(() => {
    if (!move.isMoving) return;

    const handleMouseUp = () => {
      const result = move.handleMoveEnd();

      // Set flag to prevent accidental create clicks right after moving
      justFinishedMovingRef.current = true;
      setTimeout(() => {
        justFinishedMovingRef.current = false;
      }, 100); // Small delay to ignore the mouseup click

      if (result) {
        // Add move to change queue - use unique ID
        const uniqueId = getEventUniqueId(result.event);
        moveEvent(
          uniqueId,
          result.newStart,
          result.newEnd
        );

        // Auto-delete overlapped events on the same track
        const overlappedIds = findTruckCycleOverlaps(uniqueId, result.newStart, result.newEnd);
        for (const id of overlappedIds) deleteEvent(id);
      }
      setOverlappingEventIds(new Set());
    };

    document.addEventListener("mousemove", move.handleMoveMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", move.handleMoveMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [move.isMoving, moveEvent, findTruckCycleOverlaps, deleteEvent]);

  // Handle Escape key to cancel create flow and 'V' key for video preview
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape to cancel create flow
      if (e.key === 'Escape') {
        if (createStartTime) {
          setCreateStartTime(null);
          setCreateHoverTime(null);
        }
      }

      // 'V' key to seek video to hovered event in edit mode
      if (e.key === 'v' || e.key === 'V') {
        if (isEditMode && hoveredEventForPreviewRef.current) {
          const event = hoveredEventForPreviewRef.current;
          // Calculate the middle timestamp of the event
          const startTime = new Date(event.startTime).getTime();
          const endTime = new Date(event.endTime).getTime();
          const middleTime = startTime + (endTime - startTime) / 2;

          // Seek the video to this timestamp
          setSelectedTime(middleTime, "internal");
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditMode, createStartTime, setCreateStartTime, setSelectedTime]);

  // Auto-select last event on load
  useEffect(() => {
    // Only auto-select if there's no current selection and we have tracks
    if (selectedTime !== null || tracks.length === 0) return;

    // Find the last event (latest end timestamp)
    let latestTimestamp = -Infinity;
    tracks.flat().forEach((segment) => {
      if (segment.end > latestTimestamp) {
        latestTimestamp = segment.end;
      }
    });

    // If we found a valid timestamp, select it
    if (latestTimestamp > -Infinity) {
      setSelectedTime(latestTimestamp, "external");
    }
  }, [tracks, selectedTime, setSelectedTime]);

  // Helper function to get snapped timestamp from mouse position
  const getSnappedTimestamp = useCallback((clientX: number): number | null => {
    if (!svgRef.current) return null;

    const rect = svgRef.current.getBoundingClientRect();
    const clickX = ((clientX - rect.left) / rect.width) * scaledWidth;
    let timestamp = min + (clickX / scaledWidth) * (max - min);

    // Apply snapping to the timestamp
    if (snapToBoundary) {
      // Try snapping as start time
      const snappedAsStart = snapToBoundary(timestamp, allMachineEvents, 'start');
      // Try snapping as end time
      const snappedAsEnd = snapToBoundary(timestamp, allMachineEvents, 'end');

      // Choose the closest snap point
      if (snappedAsStart !== null && snappedAsEnd !== null) {
        const distToStart = Math.abs(timestamp - snappedAsStart);
        const distToEnd = Math.abs(timestamp - snappedAsEnd);
        timestamp = distToStart < distToEnd ? snappedAsStart : snappedAsEnd;
      } else if (snappedAsStart !== null) {
        timestamp = snappedAsStart;
      } else if (snappedAsEnd !== null) {
        timestamp = snappedAsEnd;
      }
    }

    return timestamp;
  }, [svgRef, scaledWidth, min, max, snapToBoundary, allMachineEvents]);

  // Handle mouse move for preview when creating
  const handleTimelineMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    // Only show preview if in edit mode and creating (not moving/resizing)
    if (!isEditMode || move.isMoving || resize.isResizing) {
      setCreateHoverTime(null);
      return;
    }

    // Show preview if we have a start time
    if (createStartTime) {
      const timestamp = getSnappedTimestamp(e.clientX);
      if (timestamp !== null) {
        setCreateHoverTime(timestamp);
      }
    } else {
      setCreateHoverTime(null);
    }
  }, [isEditMode, createStartTime, move.isMoving, resize.isResizing, getSnappedTimestamp]);

  // Helper: check if click Y position is within the truck_cycles track
  const isClickOnTruckCyclesTrack = useCallback((clientY: number): boolean => {
    if (!svgRef.current) return false;
    const rect = svgRef.current.getBoundingClientRect();
    const clickY = ((clientY - rect.top) / rect.height) * viewBoxHeight;

    // Find the truck_cycles track index
    const truckCycleTrackIndex = tracks.findIndex(track => {
      const firstSeg = track.find(seg => seg && seg.trackKey);
      return firstSeg?.trackKey === 'truck_cycles' || firstSeg?.trackKey === 'truck-cycle';
    });

    if (truckCycleTrackIndex === -1) return false;

    const yOffset = trackRowHeights.slice(0, truckCycleTrackIndex).reduce((sum, h) => sum + h, 0);
    const rowHeight = trackRowHeights[truckCycleTrackIndex] || 58;

    return clickY >= yOffset && clickY <= yOffset + rowHeight;
  }, [svgRef, viewBoxHeight, tracks, trackRowHeights]);

  // Handle timeline click for creating new truck cycle
  const handleTimelineClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    // Only handle in edit mode when not moving/resizing
    if (!isEditMode || move.isMoving || resize.isResizing) return;

    // Don't create if we just finished moving (to prevent accidental clicks)
    if (justFinishedMovingRef.current) {
      justFinishedMovingRef.current = false;
      return;
    }

    // Check if click is on the truck_cycles track
    const onTruckTrack = isClickOnTruckCyclesTrack(e.clientY);

    // If not on truck track and not in active create flow, behave like normal (select time, show cursor)
    if (!onTruckTrack && !createStartTime) {
      cursor.handleSVGClick(e);
      return;
    }

    const timestamp = getSnappedTimestamp(e.clientX);
    if (timestamp === null) return;

    if (!createStartTime) {
      // First click on truck track - set start time
      setCreateStartTime(timestamp);
    } else {
      // Second click - set end time and create event
      if (timestamp <= createStartTime) {
        return;
      }

      // Find a truck cycle event to use as parent (for properties)
      const sampleTruckCycle = allMachineEvents.find(e =>
        (e.eventLayer?.toLowerCase() === 'truck_cycles' || e.eventLayer?.toLowerCase() === 'truck-cycle')
      );
      const parentId = sampleTruckCycle?.truckId || sampleTruckCycle?.id;

      // Create the event
      const newEventId = createEvent(createStartTime, timestamp, parentId);

      // Auto-delete overlapped events on the same track
      const overlappedIds = findTruckCycleOverlaps(newEventId, createStartTime, timestamp);
      for (const id of overlappedIds) deleteEvent(id);

      // Reset state
      setCreateStartTime(null);
      setCreateHoverTime(null);
    }
  }, [isEditMode, createStartTime, setCreateStartTime, getSnappedTimestamp, allMachineEvents, createEvent, findTruckCycleOverlaps, deleteEvent, move.isMoving, resize.isResizing, isClickOnTruckCyclesTrack, cursor]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        background: "var(--timeline-bg)",
      }}
    >
      {/* Zoom Controls */}
      {!isLive && (
      <div
        style={{
          position: "absolute",
          top: 8,
          right: 40,
          zIndex: 20,
          display: "flex",
          flexDirection: "column",
          background: "var(--timeline-bg)",
          borderRadius: 8,
          overflow: "hidden",
          boxShadow: "0 0 5px rgba(0,0,0,0.05)",
        }}
      >
        <button
          onClick={zoom.zoomIn}
          style={{
            padding: "6px 12px",
            fontSize: 16,
            background: "none",
            color: "var(--timeline-text)",
            border: "none",
            cursor: "pointer",
          }}
        >
          +
        </button>
        <button
          onClick={zoom.zoomOut}
          style={{
            padding: "6px 12px",
            fontSize: 16,
            background: "none",
            color: "var(--timeline-text)",
            border: "none",
            cursor: "pointer",
          }}
        >
          −
        </button>
      </div>
      )}

      {/* Main Timeline Container */}
      <div
        ref={containerRef}
        onWheel={isLive ? undefined : zoom.handleWheel}
        onScroll={pan.handleScroll}
        onMouseDown={isLive ? undefined : drag.onMouseDown}
        className="hide-scrollbar"
        style={{
          flex: "1 1 auto",
          overflowX: "auto",
          overflowY: "hidden",
          border: "1px solid var(--timeline-border)",
          background: "var(--timeline-bg)",
          position: "relative",
          cursor: isLive ? "default" : drag.cursor,
          userSelect: drag.userSelect,
          height: viewBoxHeight,
        }}
      >
        <svg
          ref={svgRef}
          // `timeline-refreshing` fades what is drawn while a slow refresh is
          // out, so segments that may have been overtaken read as "last known"
          // rather than as current. It never hides them: half-faded data an
          // operator can still read beats an empty box.
          className={cn(isLive && "timeline-live", showRefreshing && "timeline-refreshing")}
          width={scaledWidth}
          height={viewBoxHeight}
          viewBox={`0 0 ${scaledWidth} ${viewBoxHeight}`}
          preserveAspectRatio="none"
          style={{ width: isLive ? "100%" : scaledWidth, height: viewBoxHeight, display: "block" }}
          onMouseMove={(e) => {
            if (isEditMode) {
              handleTimelineMouseMove(e);
              // Also update hover cursor in edit mode for non-truck-track areas
              cursor.handleSVGMouseMove(e);
            } else {
              cursor.handleSVGMouseMove(e);
            }
          }}
          onMouseLeave={handleSvgMouseLeave}
          onClick={isEditMode ? handleTimelineClick : cursor.handleSVGClick}
          onDoubleClick={!isEditMode ? cursor.handleSVGDoubleClick : undefined}
        >
          {/* Loading tracks — the shape of what is coming, not a blank. Uses the
              same neutral sheen as `PendingRanges` so "waiting" reads the same
              wherever it appears. */}
          {skeletonRowCount > 0 && (
            <defs>
              <linearGradient id="timeline-skeleton-sheen" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.05" />
                <stop offset="50%" stopColor="currentColor" stopOpacity="0.13" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0.05" />
              </linearGradient>
            </defs>
          )}
          {skeletonRowCount > 0 &&
            Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
              <rect
                key={`skeleton-${rowIndex}`}
                x={0}
                y={rowIndex * SKELETON_ROW_HEIGHT + 4}
                width={scaledWidth}
                height={SKELETON_ROW_HEIGHT - 8}
                rx={3}
                fill="url(#timeline-skeleton-sheen)"
                className="text-muted-foreground"
                pointerEvents="none"
              />
            ))}

          {/* Event Tracks */}
          {tracks.map((track, rowIndex) => {
            // Skip rendering hidden tracks entirely
            const firstValidSegment = track.find(seg => seg && seg.trackKey);
            const isHidden = firstValidSegment?.hidden === true;
            if (isHidden) return null;

            const yOffset = trackRowHeights.slice(0, rowIndex).reduce((sum, h) => sum + h, 0);
            const rowHeight = trackRowHeights[rowIndex] || 58;
            const barHeight = firstValidSegment?.trackHeight || 50;
            const trackKey = firstValidSegment?.trackKey;

            // Determine if this track should be grayed out
            // In edit mode, never gray out tracks - show all events clearly
            const isTrackSelected = isEditMode ? true : (!isTrackLocked || !selectedTrack || selectedTrack === trackKey);

            return (
              <TimelineTrack
                key={rowIndex}
                track={renderTracks[rowIndex]}
                rowIndex={rowIndex}
                yOffset={yOffset}
                rowHeight={rowHeight}
                barHeight={barHeight}
                trackKey={trackKey}
                scaledWidth={scaledWidth}
                min={min}
                max={max}
                visibleRange={visibleRange}
                isTrackSelected={isTrackSelected}
                isEditMode={isEditMode}
                selectedEventForEdit={selectedEventForEdit}
                getModifiedEventTimes={getModifiedEventTimes}
                hasEventChanges={hasEventChanges}
                isResizing={resize.isResizing}
                resizingEvent={resize.resizingEvent}
                resizeTempEventTimes={resize.tempEventTimes}
                lastResizedEventId={null}
                isMoving={move.isMoving}
                movingEvent={move.movingEvent}
                moveTempEventTimes={move.tempEventTimes}
                lastMovedEventId={null}
                onEventEnter={(e, seg) => {
                  events.handleEventEnter(e, seg);
                  handleEventHoverForPreview(seg);
                }}
                onEventLeave={() => {
                  events.handleEventLeave();
                  handleEventLeaveForPreview();
                }}
                onEventClick={handleEventClick}
                onEventOpen={onEventOpen}
                onEventMove={move.handleMoveStart}
                onEventDoubleClick={handleEventDoubleClickCallback}
                svgRef={svgRef}
                onResizeStart={resize.handleResizeStart}
                overlappingEventIds={overlappingEventIds}
              />
            );
          })}

          {/* Not fetched yet — deliberately behind the segments, and only once
              the wait is long enough to be worth reading (an events fetch is
              ~250ms, so an eager skeleton would flash on every jump). */}
          <PendingRanges
            ranges={pendingRanges}
            visible={showPending}
            min={min}
            max={max}
            scaledWidth={scaledWidth}
            height={totalHeight}
          />

          {/* Video Availability Indicator (bottom track) */}
          <VideoTrack
            segments={videoSegments}
            pendingRanges={videoPendingRanges}
            min={min}
            max={max}
            scaledWidth={scaledWidth}
            viewBoxHeight={viewBoxHeight}
          />

          {/* Create Preview - Show preview rectangle when creating */}
          {isEditMode && createStartTime && createHoverTime && createHoverTime > createStartTime && (() => {
            // Find the truck_cycles track
            const truckCycleTrackIndex = tracks.findIndex(track => {
              const firstSeg = track.find(seg => seg && seg.trackKey);
              return firstSeg?.trackKey === 'truck_cycles' || firstSeg?.trackKey === 'truck-cycle';
            });

            if (truckCycleTrackIndex === -1) return null;

            const yOffset = trackRowHeights.slice(0, truckCycleTrackIndex).reduce((sum, h) => sum + h, 0);
            const rowHeight = trackRowHeights[truckCycleTrackIndex] || 58;
            const barHeight = 25; // Default truck cycle height

            const startX = timeToX(createStartTime, min, max, scaledWidth);
            const endX = timeToX(createHoverTime, min, max, scaledWidth);
            const width = endX - startX;
            const y = yOffset + (rowHeight - barHeight) / 2;

            return (
              <rect
                x={startX}
                y={y}
                width={width}
                height={barHeight}
                fill="rgba(16, 185, 129, 0.3)"
                stroke="#10b981"
                strokeWidth={2}
                strokeDasharray="5,5"
                rx={4}
                pointerEvents="none"
              />
            );
          })()}

          {/* Create Visual Indicator - Show start time line when creating */}
          {isEditMode && createStartTime && (
            <>
              {/* Start time vertical line */}
              <line
                x1={timeToX(createStartTime, min, max, scaledWidth)}
                x2={timeToX(createStartTime, min, max, scaledWidth)}
                y1={0}
                y2={viewBoxHeight}
                stroke="#10b981"
                strokeWidth={2}
                strokeDasharray="5,5"
                pointerEvents="none"
              />
              {/* Label for start time */}
              <text
                x={timeToX(createStartTime, min, max, scaledWidth)}
                y={20}
                fill="#10b981"
                fontSize={12}
                fontWeight="bold"
                textAnchor="middle"
                pointerEvents="none"
              >
                {t("timeline.startMarker")}
              </text>
            </>
          )}

          {/* Cursor Lines - Hidden in edit mode */}
          <CursorLines
            isEditMode={isEditMode}
            cursorX={cursor.cursorX}
            playbackCursorX={cursor.playbackCursorX}
            lockedCursorX={cursor.lockedCursorX}
            viewBoxHeight={viewBoxHeight}
            isVideoLinked={isVideoLinked}
            isVideoPlaying={isVideoPlaying}
            isLive={isLive}
          />

          {/* Event Tooltip */}
          <EventTooltip
            hoveredEvent={events.hoveredEvent}
            hoverPos={events.hoverPos}
            isResizing={resize.isResizing}
            isMoving={move.isMoving}
          />
        </svg>
      </div>

      {/* Time Axis */}
      {showTimeAxis && (
      <div
        ref={timeAxisRef}
        style={{
          overflow: "hidden",
          border: "1px solid var(--timeline-border)",
          background: "var(--timeline-bg)",
          whiteSpace: "nowrap",
          height: TIME_AXIS_HEIGHT,
          userSelect: "none",
        }}
      >
        <div style={{ width: isLive ? "100%" : scaledWidth, height: TIME_AXIS_HEIGHT, position: "relative" }}>
          {/* Day breaks, behind the clock labels: the axis reads HH:mm:ss and
              nothing else, so over several days 03:14 could be any night in the
              window. The rule marks the boundary, the chip names the day that
              starts there. */}
          {dayBreaks.map((day) => (
            <div
              key={day.key}
              style={{
                position: "absolute",
                left: day.x,
                top: 0,
                height: TIME_AXIS_HEIGHT,
                borderLeft: "2px solid var(--timeline-day-break)",
                paddingLeft: 4,
                display: "flex",
                alignItems: "center",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.02em",
                color: "var(--timeline-day-break)",
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              {day.label}
            </div>
          ))}
          {timeLabels.map((tick, i) => (
            <div
              // Live ticks are keyed by their timestamp so each label keeps
              // its identity and slides left as the window advances.
              key={tick.key ?? i}
              className={isLive ? "timeline-live-tick" : undefined}
              style={{
                position: "absolute",
                left: isLive ? `${(tick.x / scaledWidth) * 100}%` : tick.x,
                top: 0,
                minWidth: "80px",
                textAlign: "center",
                fontSize: 12,
                color: "var(--timeline-text)",
                transform: "translateX(-50%)",
                height: TIME_AXIS_HEIGHT,
                lineHeight: `${TIME_AXIS_HEIGHT}px`,
                whiteSpace: "nowrap",
                overflow: "visible",
              }}
            >
              {tick.label}
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Custom Scrollbar */}
      {!isLive && (
      <div style={{ height: 14, marginTop: 6 }}>
        <div
          style={{
            position: "relative",
            height: 8,
            background: "var(--timeline-border)",
            borderRadius: 4,
          }}
        >
          {(() => {
            const { thumb, left } = scrollbar.getThumbMetrics();
            return (
              <div
                onMouseDown={scrollbar.onMouseDown}
                style={{
                  position: "absolute",
                  top: 0,
                  left,
                  height: 8,
                  width: thumb,
                  background: "var(--timeline-text)",
                  borderRadius: 4,
                  cursor: "ew-resize",
                }}
              />
            );
          })()}
        </div>
      </div>
      )}

      {/* Vertical Zoom Slider */}
      {!isLive && (
      <div
        style={{
          width: "24px",
          height: `calc(100% - ${TIME_AXIS_HEIGHT}px)`,
          background: "var(--timeline-zoom-bg)",
          cursor: "ns-resize",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          userSelect: "none",
          position: "absolute",
          right: 0,
          top: 0,
          bottom: TIME_AXIS_HEIGHT,
          zIndex: 10,
        }}
        onMouseDown={(e) => zoom.handleResizeStart(e, "right")}
      >
        <div
          style={{
            height: `${zoom.getZoomPercentage() * 100}%`,
            width: "6px",
            background: "var(--timeline-text)",
            borderRadius: "3px",
            transition: "height 0.1s",
          }}
        />
      </div>
      )}
    </div>
  );
};

export default TimelineEvents;
